/**
 * 注音（Zhuyin / Bopomofo）工具模組
 *
 * 使用 pinyin-pro 取得漢字的拼音（帶聲調數字），
 * 再透過查表將拼音轉換為台灣標準注音符號。
 */
import { pinyin } from 'pinyin-pro'

// ── 拼音韻母 → 注音對照表 ──
// 聲母對照
const INITIALS_MAP: Record<string, string> = {
  b: 'ㄅ', p: 'ㄆ', m: 'ㄇ', f: 'ㄈ',
  d: 'ㄉ', t: 'ㄊ', n: 'ㄋ', l: 'ㄌ',
  g: 'ㄍ', k: 'ㄎ', h: 'ㄏ',
  j: 'ㄐ', q: 'ㄑ', x: 'ㄒ',
  zh: 'ㄓ', ch: 'ㄔ', sh: 'ㄕ', r: 'ㄖ',
  z: 'ㄗ', c: 'ㄘ', s: 'ㄙ',
  y: '', w: '',
}

// 韻母（含介音）對照
const FINALS_MAP: Record<string, string> = {
  a: 'ㄚ', o: 'ㄛ', e: 'ㄜ', i: 'ㄧ', u: 'ㄨ', v: 'ㄩ',
  ai: 'ㄞ', ei: 'ㄟ', ao: 'ㄠ', ou: 'ㄡ',
  an: 'ㄢ', en: 'ㄣ', ang: 'ㄤ', eng: 'ㄥ', er: 'ㄦ',
  ia: 'ㄧㄚ', ie: 'ㄧㄝ', iao: 'ㄧㄠ', iu: 'ㄧㄡ',
  ian: 'ㄧㄢ', in: 'ㄧㄣ', iang: 'ㄧㄤ', ing: 'ㄧㄥ',
  ua: 'ㄨㄚ', uo: 'ㄨㄛ', uai: 'ㄨㄞ', ui: 'ㄨㄟ',
  uan: 'ㄨㄢ', un: 'ㄨㄣ', uang: 'ㄨㄤ',
  ve: 'ㄩㄝ', van: 'ㄩㄢ', vn: 'ㄩㄣ',
  ue: 'ㄩㄝ', // lue, nue 的韻母
  iong: 'ㄩㄥ', ong: 'ㄨㄥ',
}

// 整音節特殊對照（零聲母或特殊拼法）
const WHOLE_SYLLABLE_MAP: Record<string, string> = {
  zhi: 'ㄓ', chi: 'ㄔ', shi: 'ㄕ', ri: 'ㄖ',
  zi: 'ㄗ', ci: 'ㄘ', si: 'ㄙ',
  yi: 'ㄧ', wu: 'ㄨ', yu: 'ㄩ',
  ye: 'ㄧㄝ', yue: 'ㄩㄝ', yuan: 'ㄩㄢ', yun: 'ㄩㄣ',
  ya: 'ㄧㄚ', yao: 'ㄧㄠ', you: 'ㄧㄡ',
  yan: 'ㄧㄢ', yin: 'ㄧㄣ', yang: 'ㄧㄤ', ying: 'ㄧㄥ',
  wa: 'ㄨㄚ', wo: 'ㄨㄛ', wai: 'ㄨㄞ', wei: 'ㄨㄟ',
  wan: 'ㄨㄢ', wen: 'ㄨㄣ', wang: 'ㄨㄤ', weng: 'ㄨㄥ',
  yong: 'ㄩㄥ',
  a: 'ㄚ', o: 'ㄛ', e: 'ㄜ', ai: 'ㄞ', ei: 'ㄟ',
  ao: 'ㄠ', ou: 'ㄡ', an: 'ㄢ', en: 'ㄣ',
  ang: 'ㄤ', eng: 'ㄥ', er: 'ㄦ',
}

// 聲調符號
const TONE_MARKS: Record<number, string> = {
  1: '', // 一聲不標
  2: 'ˊ',
  3: 'ˇ',
  4: 'ˋ',
  5: '˙', // 輕聲
  0: '',
}

/**
 * 將帶聲調數字的拼音（如 "tian1"）轉換為注音（如 "ㄊㄧㄢ"）
 */
function pinyinToZhuyin(pinyinWithTone: string): string {
  // 拆分音節與聲調
  const match = pinyinWithTone.match(/^([a-z]+)(\d)?$/)
  if (!match) return ''

  let syllable = match[1]
  const tone = parseInt(match[2] || '0', 10)
  const toneMark = TONE_MARKS[tone] || ''

  // 先處理 ü → v 的情況（lv, nv, lve, nve）
  syllable = syllable.replace('ü', 'v')

  // 先查整音節表
  if (WHOLE_SYLLABLE_MAP[syllable] !== undefined) {
    const result = WHOLE_SYLLABLE_MAP[syllable]
    // 輕聲標在前面
    if (tone === 5) return '˙' + result
    return result + toneMark
  }

  // 拆分聲母和韻母
  let initial = ''
  let final = ''

  // 嘗試匹配雙字母聲母（zh, ch, sh）
  const twoLetterInitial = syllable.slice(0, 2)
  if (['zh', 'ch', 'sh'].includes(twoLetterInitial)) {
    initial = twoLetterInitial
    final = syllable.slice(2)
  } else {
    const oneLetterInitial = syllable[0]
    if (INITIALS_MAP[oneLetterInitial] !== undefined && oneLetterInitial !== 'a' && oneLetterInitial !== 'e' && oneLetterInitial !== 'o') {
      initial = oneLetterInitial
      final = syllable.slice(1)
    } else {
      // 零聲母
      initial = ''
      final = syllable
    }
  }

  // j, q, x 後面的 u 實際上是 ü
  if (['j', 'q', 'x'].includes(initial) && final.startsWith('u')) {
    final = 'v' + final.slice(1)
  }

  const initialZhuyin = initial ? (INITIALS_MAP[initial] || '') : ''
  const finalZhuyin = FINALS_MAP[final] || ''

  if (!finalZhuyin && !initialZhuyin) return ''

  const result = initialZhuyin + finalZhuyin
  // 輕聲標在前面
  if (tone === 5) return '˙' + result
  return result + toneMark
}

// 快取，避免重複計算
const cache = new Map<string, string>()

/**
 * 取得單一漢字的注音符號
 * @param char 單一漢字
 * @returns 注音字串，如 "ㄊㄧㄢ"；若非漢字則回傳空字串
 */
export function getZhuyin(char: string): string {
  if (!char || char.length === 0) return ''

  // 只處理 CJK 統一漢字範圍的字元
  const code = char.charCodeAt(0)
  const isCJK =
    (code >= 0x4E00 && code <= 0x9FFF) ||   // CJK Unified Ideographs
    (code >= 0x3400 && code <= 0x4DBF) ||   // CJK Extension A
    (code >= 0xF900 && code <= 0xFAFF)       // CJK Compatibility Ideographs
  if (!isCJK) return ''

  if (cache.has(char)) return cache.get(char)!

  try {
    const py = pinyin(char, { toneType: 'num', type: 'array' })
    if (py && py.length > 0) {
      const zhuyin = pinyinToZhuyin(py[0])
      cache.set(char, zhuyin)
      return zhuyin
    }
  } catch {
    // 靜默處理
  }

  cache.set(char, '')
  return ''
}

/**
 * 批次取得多個漢字的注音（用於預先載入快取）
 * @param text 文字字串
 * @returns 以 Map<字元, 注音> 形式回傳
 */
export function batchGetZhuyin(text: string): Map<string, string> {
  const result = new Map<string, string>()
  for (const char of text) {
    if (!result.has(char)) {
      result.set(char, getZhuyin(char))
    }
  }
  return result
}
